import React from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const SERIES = [
  { key: "spas-beauty", name: "Spas & Beauty", color: "#D4A24A" },
  { key: "hair-salons", name: "Hair Salons", color: "#F5EBD0" },
  { key: "nail-salons", name: "Nail Salons", color: "#C9786B" },
  { key: "real-estate", name: "Real Estate", color: "#8FB8A8" },
  { key: "hvac", name: "HVAC", color: "#7FA3C9" },
  { key: "plumbing", name: "Plumbing", color: "#A3A86B" },
  { key: "none", name: "No industry", color: "#6B6B6B", dashed: true },
];
const METRICS = [
  { key: "calendly_cta_click", label: "Calendly CTA Clicks" },
  { key: "industry_card_click", label: "Card Clicks" },
  { key: "lead_gen_example_view", label: "Example Views" },
  { key: "industry_page_view", label: "Page Visits" },
];
const AUTO_DAILY_MAX_DAYS = 45;
const DAY_MS = 86400000;

const utcDate = (iso) => new Date(`${iso}T00:00:00Z`);
const iso = (d) => d.toISOString().slice(0, 10);
const weekStart = (d) => new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * DAY_MS); // Monday

// Zero-filled buckets from recorded events only.
function buildRows(trend, range, metric, weekly) {
  const rows = new Map();
  for (let d = utcDate(range.start); d <= utcDate(range.end); d = new Date(d.getTime() + DAY_MS)) {
    const key = iso(weekly ? weekStart(d) : d);
    if (!rows.has(key)) rows.set(key, Object.fromEntries([["label", key], ...SERIES.map((s) => [s.key, 0])]));
  }
  for (const t of trend) {
    if (t.event_type !== metric) continue;
    const row = rows.get(iso(weekly ? weekStart(utcDate(t.date)) : utcDate(t.date)));
    if (row) row[t.industry || "none"] += t.count;
  }
  return [...rows.values()];
}

function Toggle({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button key={o.key} onClick={() => onChange(o.key)} data-testid={`${testid}-${o.key}`}
          aria-pressed={value === o.key} className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] border transition-colors ${value === o.key ? "bg-brand border-transparent text-black" : "border-white/10 text-secondary hover:text-primary"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function TrendChart({ trend, range }) {
  const days = Math.round((utcDate(range.end) - utcDate(range.start)) / DAY_MS) + 1;
  const [metric, setMetric] = React.useState("calendly_cta_click");
  const [grouping, setGrouping] = React.useState("auto");
  const [hidden, setHidden] = React.useState(() => new Set());
  const weekly = grouping === "auto" ? days > AUTO_DAILY_MAX_DAYS : grouping === "weekly";
  const rows = React.useMemo(() => buildRows(trend, range, metric, weekly), [trend, range, metric, weekly]);
  const toggleSeries = ({ dataKey }) => setHidden((h) => { const n = new Set(h); n.has(dataKey) ? n.delete(dataKey) : n.add(dataKey); return n; });

  return (
    <section className="mt-10 editorial-card p-6" data-testid="admin-trend-chart">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl">Trend by Industry</h2>
          <p className="text-secondary text-xs mt-2" data-testid="admin-trend-note">
            {metric === "calendly_cta_click"
              ? "Calendly CTA clicks show booking interest only. They are not confirmed bookings."
              : "Recorded anonymous events."} {weekly ? "Grouped by week (Mon)." : "Grouped by day."} Click a legend item to hide or show it.
          </p>
        </div>
        <Toggle testid="admin-trend-grouping" value={grouping} onChange={setGrouping}
          options={[{ key: "auto", label: `Auto` }, { key: "daily", label: "Daily" }, { key: "weekly", label: "Weekly" }]} />
      </div>
      <div className="mt-4"><Toggle testid="admin-trend-metric" value={metric} onChange={setMetric} options={METRICS} /></div>
      <div className="mt-6 h-80" data-testid="admin-trend-canvas">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
            <CartesianGrid stroke="#2a2418" vertical={false} />
            <XAxis dataKey="label" stroke="#B89968" tick={{ fontSize: 11 }} tickLine={false} minTickGap={24} />
            <YAxis allowDecimals={false} stroke="#B89968" tick={{ fontSize: 11 }} tickLine={false} />
            <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #2a2418", fontSize: 12 }} labelStyle={{ color: "#D4A24A" }}
              labelFormatter={(l) => (weekly ? `Week of ${l}` : l)} />
            <Legend onClick={toggleSeries} wrapperStyle={{ fontSize: 11, cursor: "pointer" }}
              formatter={(value, entry) => <span style={{ opacity: hidden.has(entry.dataKey) ? 0.35 : 1 }}>{value}</span>} />
            {SERIES.map((s) => (
              <Line key={s.key} dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2} dot={false}
                strokeDasharray={s.dashed ? "4 4" : undefined} hide={hidden.has(s.key)} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
